import StoreBadges from '../../components/StoreBadges';
import usePageMeta from '../../utils/usePageMeta';
import './Apps.css';

const CER_FEATURES = [
  {
    icon: '/icons/apps/Icon-7.svg',
    title: 'Gestión online 24/7',
    desc: 'Accede a toda la info de tus departamentos en cualquier momento',
  },
  {
    icon: '/icons/apps/Icon-3.svg',
    title: 'Descarga de Liquidaciones',
    desc: 'Bajá tus recibos y liquidaciones en PDF con un click',
  },
  {
    icon: '/icons/apps/Icon-2.svg',
    title: 'Historial de pagos',
    desc: 'Consultá todos tus recibos desde la app',
  },
  {
    icon: '/icons/apps/Icon-1.svg',
    title: 'Votaciones',
    desc: 'Participá de votaciones y encuestas del consorcio',
  },
  {
    icon: '/icons/apps/Icon.svg',
    title: 'Reserva de amenities',
    desc: 'Reservá el SUM, pileta y otros espacios comunes de forma sencilla',
  },
];

const NUMBERED_FEATURES = [
  {
    n: '01',
    title: 'Gestiona las reservas',
    desc: 'Reserva en simples pasos y desde una interfaz rápida y eficiente, cupos en tiempo real, costos y términos de condiciones a la hora de reservas, todo detallado para facilitar las cosas',
  },
  {
    n: '02',
    title: 'Descarga las facturas',
    desc: 'Desde la app vas a poder ver todas las facturas asociadas a los gastos de cada liquidación, separadas por meses y vas a poder descargarlas',
  },
  {
    n: '03',
    title: 'Sección de Reclamos',
    desc: 'Vas a poder habilitar una sección especial para que los propietarios vean o suban los reclamos pendientes de resolución del edificio y de sus unidades funcionales, así mismo vas a poder editar el estado de los mismos para llevar un control',
  },
];

const ADA5WEB_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.loricode.Ada5Web';
const CER_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.loricode.ada5mobilepropietario';
const CER_APPLE_URL = 'https://apps.apple.com/ar/app/consorciosenred/id6762222689';

const ADA5WEB_FEATURES = [
  {
    icon: '/icons/apps/Icon-10.svg',
    title: 'Liquidación de expensas',
    desc: 'Carga gastos y cobranzas de forma rápida y sencilla.',
  },
  {
    icon: '/icons/apps/page-search.svg',
    title: 'Consulta historicos',
    desc: 'Podes consultar el historico de expensas y recibos de cada unidad.',
  },
  {
    icon: '/icons/apps/Icon-6.svg',
    title: 'Carga de documentacion',
    desc: 'Podes subir documentacion y asambleas y generar reportes de balances.',
  },
  {
    icon: '/icons/apps/Icon-4.svg',
    title: 'Envio de comunicados',
    desc: 'Envía de forma rápida comunicados a tus consorcios o proveedores.',
  },
];

function FeatureCardList({ features }) {
  return (
    <ul className="feature-cards">
      {features.map((f) => (
        <li key={f.title} className="feature-cards__item">
          <div className="feature-cards__icon">
            <img src={f.icon} alt="" aria-hidden="true" />
          </div>
          <div className="feature-cards__copy">
            <h3>{f.title}</h3>
            <p>{f.desc}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function Apps() {
  usePageMeta({
    title: 'Nuestras Apps — Sistemas Administrativos S.A.',
    description:
      'Apps móviles para administradores y propietarios: ConsorciosEnRed+ y ADA5WEB. Disponible en App Store y Google Play.',
  });

  return (
    <div className="apps-page">
      {/* HERO */}
      <section className="apps-hero">
        <div className="container">
          <h1 className="apps-hero__title">NUESTRAS APPS</h1>
          <p className="apps-hero__subtitle">
            Nos renovamos con la app más completa para el consorcista, acá podrá ver sus
            liquidaciones, informar pagos, reservar amenities y mucho más de manera simplificada
          </p>

          <div className="apps-hero__mockups">
            <img src="/images/apps/apps-hero-mockup-left.png" alt="iPhone mockup" />
            <img
              src="/images/apps/apps-hero-mockup-center.png"
              alt="iPhone mockup centro"
              className="apps-hero__mockups-center"
            />
            <img src="/images/apps/apps-hero-mockup-right.png" alt="iPhone mockup" />
          </div>
        </div>
      </section>

      {/* CONSORCIOS EN RED+ */}
      <section className="apps-app-section apps-app-section--cer">
        <div className="container apps-app-section__inner">
          <div className="apps-app-section__copy">
            <h2 className="apps-app-section__title apps-app-section__title--cer">
              Consorcios en Red+
            </h2>
            <p className="apps-app-section__desc">
              La app donde los propietarios pueden ver sus expensas, recibos, comunicaciones,
              participar en votaciones, reservar amenities y avisar su pago — todo desde el celular.
            </p>

            <FeatureCardList features={CER_FEATURES} />

            <div className="apps-app-section__avail">
              <span>Disponible en:</span>
              <StoreBadges appStoreUrl={CER_APPLE_URL} googlePlayUrl={CER_PLAY_URL} />
            </div>
          </div>

          <div className="apps-app-section__visual">
            <img src="/images/apps/apps-cer-mockup.png" alt="Consorcios en Red+" />
          </div>
        </div>
      </section>

      {/* NUMBERED FEATURES (01-03) — extras de ConsorciosEnRed+ */}
      <section className="apps-numbered">
        <div className="container">
          <ol className="numbered-list">
            {NUMBERED_FEATURES.map((f) => (
              <li key={f.n} className="numbered-list__item">
                <span className="numbered-list__num">{f.n}</span>
                <div className="numbered-list__copy">
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ADA5WEB */}
      <section className="apps-app-section apps-app-section--ada">
        <div className="container apps-app-section__inner apps-app-section__inner--reverse">
          <div className="apps-app-section__visual">
            <img src="/images/apps/apps-ada5web-mockup.png" alt="ADA5WEB" />
          </div>

          <div className="apps-app-section__copy">
            <h2 className="apps-app-section__title apps-app-section__title--ada">ADA5WEB</h2>
            <p className="apps-app-section__desc">
              Ahora podes llevar en tu bolsillo la gestion de tus edificios, realiza consultas,
              carga datos y envia comunicados todo, desde la comodidad de tu telefono
            </p>

            <FeatureCardList features={ADA5WEB_FEATURES} />

            <div className="apps-app-section__avail">
              <span>Disponible en:</span>
              <StoreBadges appStoreUrl={null} googlePlayUrl={ADA5WEB_PLAY_URL} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
